import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useApi } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { PageHeader } from '@/shared/components';
import { useOnline } from '@/shared/hooks/useOnline';
import { CleaningScopePicker, CleaningScopeRequired } from '../CleaningScopeRequired';
import {
  addDaysIso,
  parseCleaningDate,
  todayInTimeZone,
} from '../calendar/cleaningCalendarHelpers';
import { CleaningDateNav, dayTitle } from '../CleaningDateNav';
import { useCleaningScope } from '../useCleaningScope';
import { MyDayList, type StepFailure } from './MyDayList';
import { useTaskStep } from './useTaskStep';

/**
 * „Meine Aufgaben“ (Board B): der Tagesplan der Reinigungskraft. Dieselbe Tagesliste wie für die
 * Leitung, nur aus ihrer Sicht geordnet — was sie tun darf, sagen die Flags je Aufgabe.
 */
export function MyTasksPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_admin/cleaning' });
  const { user } = useAuth();
  const online = useOnline();
  const { group, groups, isLoading, isError, retry, setGroupId, usesLocalScope } =
    useCleaningScope();
  const today = group ? todayInTimeZone(group.timezone) : '1970-01-01';
  const date = parseCleaningDate(search.date, today);
  const [failure, setFailure] = useState<StepFailure | null>(null);
  const step = useTaskStep({
    onFailure: (message, vars) =>
      setFailure({ id: vars.task.id, name: vars.task.propertyName, message }),
  });
  const propertyIds = group?.apartments.map((apartment) => apartment.id) ?? [];
  const day = useQuery({
    queryKey: ['cleaning', 'day', group?.id, date],
    queryFn: () => api.cleaning.list({ scheduledFor: date, propertyIds }),
    enabled: Boolean(group),
  });

  const setDate = (next: string) => {
    setFailure(null);
    void navigate({
      to: '/cleaning',
      search: { ...search, date: next === today ? undefined : next },
    });
  };

  if (!group)
    return (
      <CleaningScopeRequired
        groups={groups}
        loading={isLoading}
        error={isError}
        select={setGroupId}
        retry={retry}
      />
    );
  return (
    <div className="mx-auto w-full max-w-xl">
      <PageHeader
        className="mb-3"
        title="Meine Aufgaben"
        tools={
          <CleaningDateNav
            label={dayTitle(date, today)}
            unit="day"
            date={date}
            showToday={date !== today}
            onShift={(direction) => setDate(addDaysIso(date, direction))}
            onPick={setDate}
            onToday={() => setDate(today)}
          />
        }
      />
      {usesLocalScope && groups.length > 1 && (
        <div className="mb-3">
          <CleaningScopePicker groups={groups} selectedId={group.id} select={setGroupId} />
        </div>
      )}
      {!online && (
        <p
          role="status"
          className="bg-amber-tint text-amber-ink rounded-tile mb-3 p-3 text-[13px] font-semibold"
        >
          Keine Verbindung. Übernehmen und „Erledigt melden“ gehen erst wieder mit Netz.
        </p>
      )}
      <MyDayList
        query={day}
        dayLabel={dayTitle(date, today).split(' · ')[0] ?? date}
        today={today}
        userId={user?.userId}
        timeZone={group.timezone}
        online={online}
        busyId={step.isPending ? step.variables?.task.id : undefined}
        failure={failure}
        onDismissFailure={() => setFailure(null)}
        onStep={(vars) => {
          setFailure(null);
          step.mutate(vars);
        }}
        onNextDay={() => setDate(addDaysIso(date, 1))}
      />
    </div>
  );
}
